import { useRef, type KeyboardEvent, type MouseEvent } from 'react';

import { focusRing } from '@/design-system';
import { cn } from '@/shared/lib';

import type { PublicCopy } from '../copy';
import { isModifiedClick } from '../jump';
import { reportPanelId, reportTabId, type ReportTab } from '../useReportView';

export interface ReportTabsProps {
    /** De tabbladen die dit rapport heeft, in hun vaste volgorde. */
    tabs: readonly ReportTab[];
    tab: ReportTab;
    onSelect: (tab: ReportTab) => void;
    copy: PublicCopy;
    /** Voor de `id`'s van de tabs en het paneel dat ze tonen. */
    idPrefix: string;
    /**
     * Blijven de tabbladen bovenaan staan tijdens het lezen? Op de klantpagina
     * wel. In de kleine voorvertoning van kantoor niet: daar is de pagina zelf
     * niet de scroller, en dan plakt de balk aan het venster in plaats van aan
     * het kader — dat is de scheve kop die over de tekst heen viel.
     */
    pinned?: boolean;
}

/**
 * De tabbladen onder de bedrijfsbalk: Overzicht, Kosten, Rapport en NHG. Ze
 * blijven bovenaan staan tijdens het lezen, behalve waar de pagina in een kader
 * van kantoor staat ({@link ReportTabsProps.pinned}).
 *
 * Tabs volgens WAI-ARIA: pijltjes, Home en End wisselen, en alleen het gekozen
 * tabblad staat in de tabvolgorde. Elk tabblad is ook een link (`#kosten`), zodat
 * kopiëren en openen in een nieuw tabblad van de browser werken.
 */
export function ReportTabs({
    tabs,
    tab,
    onSelect,
    copy,
    idPrefix,
    pinned = true,
}: ReportTabsProps) {
    const links = useRef<Partial<Record<ReportTab, HTMLAnchorElement | null>>>({});
    const labels: Record<ReportTab, string> = {
        overzicht: copy.tabOverview,
        kosten: copy.tabCosts,
        rapport: copy.tabReport,
        nhg: copy.tabNhg,
    };

    const select = (next: ReportTab) => {
        if (next !== tab) {
            onSelect(next);
        }
    };

    const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        const index = tabs.indexOf(tab);
        const next =
            event.key === 'ArrowRight'
                ? tabs[(index + 1) % tabs.length]
                : event.key === 'ArrowLeft'
                  ? tabs[(index - 1 + tabs.length) % tabs.length]
                  : event.key === 'Home'
                    ? tabs[0]
                    : event.key === 'End'
                      ? tabs[tabs.length - 1]
                      : event.key === ' '
                        ? tab
                        : undefined;

        if (next === undefined) {
            return;
        }

        event.preventDefault();
        select(next);
        links.current[next]?.focus();
    };

    return (
        <div
            role="tablist"
            aria-label={copy.tabsLabel}
            onKeyDown={onKeyDown}
            className={cn(
                'flex min-h-[49px] gap-1 overflow-x-auto border-b border-[var(--border-subtle)] bg-[var(--surface-card)] px-[calc(var(--pr-pad-x)_-_12px)] [scrollbar-width:none]',
                pinned && 'sticky top-0 z-20',
            )}
        >
            {tabs.map((value) => {
                const selected = value === tab;

                return (
                    <a
                        key={value}
                        ref={(element) => {
                            links.current[value] = element;
                        }}
                        href={`#${value}`}
                        role="tab"
                        id={reportTabId(idPrefix, value)}
                        aria-selected={selected}
                        aria-controls={reportPanelId(idPrefix)}
                        tabIndex={selected ? 0 : -1}
                        onClick={(event: MouseEvent<HTMLAnchorElement>) => {
                            if (isModifiedClick(event)) {
                                return;
                            }

                            event.preventDefault();
                            select(value);
                        }}
                        className={cn(
                            '-mb-px inline-flex min-h-12 shrink-0 items-center border-0 border-b-2 border-solid px-3 no-underline',
                            'font-[family-name:var(--font-body)] text-[14px] leading-[1.25] font-medium whitespace-nowrap',
                            'transition-colors duration-100',
                            selected
                                ? 'border-[var(--accent)] text-[var(--text-primary)]'
                                : 'border-transparent text-[var(--text-tertiary-safe)] hover:text-[var(--text-primary)]',
                            focusRing,
                            'focus-visible:-outline-offset-2',
                        )}
                    >
                        {labels[value]}
                    </a>
                );
            })}
        </div>
    );
}
